import { cloneDeep } from 'lodash';
import { CheckBoxSelector } from '../../shared/component/checkbox-selector/checkbox-selector.component';
import { Organization } from 'src/app/domain/organization/dto/Organization';
import { FpCardValueHeader } from "../../fund-performance/component/fund-performances/pojo/fp-export-classes";
import { LocalDate } from "../../../core/pojo/date-variable";
import { SwitcherNumberType } from "../../../core/component/numberswitcher/utils/number-switcher-common-classes";
import { IrTabsEnum } from "./ir-enums";
import { DefaultBulkUploadShow } from "../../bulk-upload/pojo/default-bulk-upload-pojo";
import {
  IrPortfolioSortingEnum, IrPortfolioSortingOrderEnum,
} from "../component/ir/ir-sub-components/ir-portfolio-summary-table/pojo/ir-summary-table-pojo";
import { FundPerformance } from '../../fund-performance/dto/FundPerformance';

export class IrSetUp {
  fundUUID: string = '';
  period: LocalDate;
  isEditMode: boolean = false;
  forPortal: boolean = false; // IR preview page opened from portal side
  password: string | null = null;
  token: string | null = null;

  constructor(init?: Partial<IrSetUp>) {
    Object.assign(this, init);
  }
}

export class InvestorReportDTO {
  id?: number;
  uuid?: string;
  fundUuid?: string;
  period?: LocalDate;
  lastModifiedDate?: Date;
  publishProps = new PublishPropsDTO();
  reportNotes = new ReportNotes();
  fpOverviewSection = new FpOverviewSectionDTO();
  irPortfolioTable = new IrPortfolioTableDTO();
  fmvChartsSection = new IrFmvChartsSectionDTO();
  valueCreationSection = new IrValueCreationSectionDTO();
  irCompanyOverviewSection = new IrCompanyOverviewSectionDTO();
  investorPositionSection = new InvestorPositionSectionDTO();
  selectedPortfolioIds: number[];


  /**
   * Deserialize data into an instance of InvestorReportDTO.
   *
   * @param data Raw data to deserialize.
   * @returns Deserialized instance of InvestorReportDTO.
   */
  public static deserialize(data: any): InvestorReportDTO {
    const irData = new InvestorReportDTO();
    Object.assign(irData, data);
    irData.fpOverviewSection = Object.assign(new FpOverviewSectionDTO(), data.fpOverviewSection);
    irData.irPortfolioTable = Object.assign(new IrPortfolioTableDTO(), data.irPortfolioTable);
    irData.irCompanyOverviewSection = Object.assign(new IrCompanyOverviewSectionDTO(), data.irCompanyOverviewSection);
    return irData;
  }
}

export class PublishedInvestorReport {
  // PublishedInvestorReportDTO in the backend
  fundName: string;
  orgLogo: string;
  isLinkPublic: boolean;
  forPortal: boolean;

  // FullPublishedInvestorReportDTO in the backend
  ir: InvestorReportDTO;
  fpCards: IrFundPerformanceCardsDTO;
  pieChartsCards: IrPieChartsRawDataDTO;
  selectedPps: IrPortfoliosDTO;
  companiesData: IrCompanyDTO[];
}

export class IrWithPeriodsDTO {
  ir = new InvestorReportDTO();
  periodsInfo = new IrPeriods();
  fp: FundPerformance;
  fpCards: IrFundPerformanceCardsDTO;
  pieChartsCards: IrPieChartsRawDataDTO;
  pps: IrPortfoliosDTO;
  selectedPps: IrPortfoliosDTO;
  companiesData: IrCompanyDTO[];
}

export interface IrPieChartsRawDataDTO {
  totalCommitment: number;
  currentInvestment: number;
  newInvestmentAllocation: number;
  currentExpenses: number;
  expectedFutureExpenses: number;
  unallocatedCommitment: number;
  aboveCommitment: number | null;
  hasAboveCommitment: boolean;
  totalExpenses: number;
  mgmFees: number;
  aggregatedOrgExpenses: number;
  otherExpenses: number;
}


export interface IrFundPerformanceCardsDTO {
  // --- TOTAL ---
  totalInvestment: number;
  totalFmv: number;
  totalGain: number;
  totalCompaniesAmount: number;
  totalMoic: number;
  totalIrr: number;

  // --- REALIZED ---
  realizedInvestment: number;
  realizedFmv: number;
  realizedGain: number;
  realizedCompaniesAmount: number;
  realizedMoic: number;
  realizedIrr: number;

  // --- UNREALIZED ---
  unrealizedInvestment: number;
  unrealizedFmv: number;
  unrealizedGain: number;
  unrealizedCompaniesAmount: number;
  unrealizedMoic: number;
  unrealizedIrr: number;

  // --- BEFORE CARRY ---
  beforeCarryContribution: number;
  beforeCarryNavAndDistribution: number;
  beforeCarryNetGainLoss: number;
  beforeCarryFundSize: number;
  beforeCarryTvpi: number;
  beforeCarryIrr: number;
  beforeCarryDistribution: number;
  beforeCarryDpi: number;
  beforeCarryNav: number;
  beforeCarryRvpi: number;

  // --- AFTER CARRY ---
  afterCarryContribution: number;
  afterCarryNavAndDistribution: number;
  afterCarryNetGainLoss: number;
  afterCarryFundSize: number;
  afterCarryTvpi: number;
  afterCarryIrr: number;
  afterCarryDistribution: number;
  afterCarryDpi: number;
  afterCarryNav: number;
  afterCarryRvpi: number;
}


export class ReportNotes {
  disclaimerInput = '';
  disclaimerTitle = '';
  isDisclaimerSelected = true;
  noteInput = '';
  noteTitle = '';
  isNoteSelected = true;
}

export class IrDisclaimer {
  isIncluded = false;
  input = '';
  title = '';
}

export class IrPassword {
  visible = false;
  inputType: 'password' | 'text' = 'password';
}

export class PublishPropsDTO {
  published = false;
  withLink? = false;
  link: string;
  password: string;
}

export class FpOverviewSectionDTO {
  overviewSectionName: string;

  isOverviewSectionSelected = true;
  isTotalPpSelected = true;
  isRealizedPpSelected = true;
  isUnrealizedPpSelected = true;
  isNetFpBeforeCarrySelected = true;
  isNetFpAfterCarrySelected = true;

  isCommitmentAllocationSelected = true;
  isOpexDrilldownSelected = true;
}


export class IrPortfolioTableDTO {
  portfolioSummarySectionName: string;

  isPortfolioTableSelected = true;
  switcher: SwitcherNumberType;
  selectedSorting: IrPortfolioSortingEnum;
  selectedSortingOrder: IrPortfolioSortingOrderEnum;

  isPpIndustrySelected = true;
  isPpCountrySelected = false;
  isPpCompanyGroupSelected = false;
  isPpInitialDateSelected = true;
  isPpCostSelected = true;
  isPpFairValueSelected = true;
  isPpGainLossSelected = true;
  isPpHoldingSelected = true;
  isPpMultipleSelected = true;
  isPpIrrSelected = true;
  isPpRaisedSelected = true;
  isPpValuationSelected = true;
}

export class IrCompanyOverviewSectionDTO {

  isCompanyOverviewSelected = true;
  companiesSectionName: string;
  selectedCompaniesIdsAndNotes: CompanyIdAndNoteDTO[] = [];

  isCompanyStatusSelected = true;
  isCompanyCountrySelected = true;
  isCompanyIndustrySelected = true;
  isCompanyStockTickerSelected = true;
  isCompanyWebsiteSelected = true;
  isCompanyDescriptionSelected = true;
  isCompanyInvestmentSelected = true;
  isCompanyFairValueSelected = true;
  isCompanyGainSelected = true;
  isCompanyMoicSelected = true;
  isCompanyHoldingSelected = true;
  isCompanyIrrSelected = true;
  isCompanyCashReserveSelected = true;
  isCompanyBurnRateSelected = true;
  isCompanyArrSelected = true;
  isCompanyNoteSelected = true;
}

export class CompanyIdAndNoteDTO {
  selectedCompanyId: number;
  selected: boolean;
  note: string;
  noteTitle: string;
}

export class IrPortfoliosDTO {
  all: IrPortfolioPerformanceDTO[] = [];
  active: IrPortfolioPerformanceDTO[] = [];
  sold: IrPortfolioPerformanceDTO[] = [];
  totalActive?: IrPortfolioPerformanceDTO;
  totalSold?: IrPortfolioPerformanceDTO;
  grandTotal?: IrPortfolioPerformanceDTO;
}
export class IrPortfolioPerformanceDTO {
  id: number;
  name: string;
  country: string;
  industry: string;
  companyGroup: string;
  dateOfInitialInvestment: LocalDate;
  cost: number;
  fairValue: number;
  gainLoss: number;
  holdingPercentage: number;
  multiple: number;
  calculatedGrossIrr: number;
  totalInvestmentInTheCompany: number;
  valuationRoundValue: number;
}


const USER_PORTAL_LABEL = 'user-portal-label.svg; icon-size-w-5-h-1p25';
const PUBLIC_LINK_LABEL = 'public-link-label.svg; main-8; icon-size-w-5-h-1p25';
const OPACITY = 'opacity0p25';

export class IrPeriods {
  selected = new IrSelectorPeriod();
  fromSelector?: IrSelectorPeriod[] = [];

  updateSelectedImgs(publishProps: PublishPropsDTO): void {
    this.selected.iconNameAndStyling1 = publishProps.published
      ? USER_PORTAL_LABEL
      : `${USER_PORTAL_LABEL}; ${OPACITY}`;
    this.selected.iconNameAndStyling2 = publishProps.withLink
      ? PUBLIC_LINK_LABEL
      : `${PUBLIC_LINK_LABEL}; ${OPACITY}`;
    this.selected = { ...this.selected }; // Necessary to rerender icons after share settings are updated
  }

  placeAllImgs(): void {
    this.fromSelector?.forEach((p) => {
      if (p.saved) {
        p.iconNameAndStyling1 = p.published
          ? USER_PORTAL_LABEL
          : `${USER_PORTAL_LABEL}; ${OPACITY}`;
        p.iconNameAndStyling2 = p.withLink
          ? PUBLIC_LINK_LABEL
          : `${PUBLIC_LINK_LABEL}; ${OPACITY}`;
      }
    });

    if (this.selected.saved) {
      this.selected.iconNameAndStyling1 = this.selected.published
        ? USER_PORTAL_LABEL
        : `${USER_PORTAL_LABEL}; ${OPACITY}`;
      this.selected.iconNameAndStyling2 = this.selected.withLink
        ? PUBLIC_LINK_LABEL
        : `${PUBLIC_LINK_LABEL}; ${OPACITY}`;
    }
  }

  updateAllPeriodsArray(selectedPeriod: IrSelectorPeriod): void {
    const periodToUpdate = this.fromSelector.find((p) => p.period === selectedPeriod.period);
    if (periodToUpdate) {
      Object.assign(periodToUpdate, selectedPeriod);
    }
    this.fromSelector = cloneDeep(this.fromSelector); // is necessary to rerender icons after share settings are updated
  }
}

export class IrSelectorPeriod {
  period: string;
  saved: boolean;
  published: boolean;
  withLink: boolean;
  iconNameAndStyling1?: string;
  iconNameAndStyling2?: string;
}

/**
 * Company data from backend. Contains PPs related to each company.
 */
export class IrCompanyDTO {
  main: IrCompanyMainDTO;
  pps: IrCompanyPpsDTO[];
  partialSoldFinData: IrCompanyPpsDTO; // contains merged fin data in case pps.length = 2
  note: string;
  noteTitle: string;
}

export class IrCompanyMainDTO {
  companyId: number;
  logo: string;
  name: string;
  industry: string;
  country: string;
  stockLink: string;
  stockTicker: string;
  description: string;
  webSite: string;
}

export class IrCompanyPpsDTO {
  id: number;
  companyId: number;
  isRealized: boolean;
  cost: number;
  fairValue: number;
  gainLoss: number;
  multiple: number;
  holdingPercentage: number;
  calculatedGrossIrr: number;
  cashReserveAmount: number;
  burnRate: number;
  revenueAnnual: number;
}

/**
 * Company data which is used to present information to the user in the frontend
 */
export class IrCompaniesData {
  general?: IrCompanyMainDTO;
  financial?: CompanyFinancialData;
  note?: string;
  noteTitle?: string;
  status?: IrCompaniesStatus;
}

export class CompanyFinancialData {
  companyId = 0;
  investment = 0;
  fairValue = 0;
  moic = 0;
  gain = 0;
  currentHolding = 0;
  irr = 0;
  cashReserve = 0;
  burnRate = 0;
  arr = 0;
}

export class IrSelected {
  org: Organization;
  tab: IrTabsEnum = IrTabsEnum.FUND_POSITION;
  fmvCheckboxes: CheckBoxSelector[];
  valueCreationPeriods: CheckBoxSelector[];
  companiesCheckboxes: CheckBoxSelector[];
  portfolioSortSelector: IrPortfolioSortingEnum[] = Object.values(IrPortfolioSortingEnum);
  portfolioSortOrderSelector: IrPortfolioSortingOrderEnum[] = Object.values(IrPortfolioSortingOrderEnum);
}

export class IrIs {
  admin = false;
  withPortalAccess = false;
}

export class IrShow {
  publishModal = false;
  disclaimer = false;
  note = false;
}

export class ShowPublishedIr {
  passwordModal = false;
  disclaimer = false;
  emptyState = false;
  captchaV2 = false;
}

export class IrFpCardsShow {
  partiallyRealizedCards = false;
}

export class IrFpCardsDataDTO {
  constructor(
    public total: IrFpCardsWithChart = new IrFpCardsWithChart(),
    public realized: IrFpCardsWithChart = new IrFpCardsWithChart(),
    public unRealized: IrFpCardsWithChart = new IrFpCardsWithChart(),
    public beforeCarry: IrFpCardsWithChart = new IrFpCardsWithChart(),
    public afterCarry: IrFpCardsWithChart = new IrFpCardsWithChart(),
  ) {}
}

export class IrFpCardsWithChart {
  constructor(
    public cards: IrFpCard[] = [],
    public chart: IrFpChart = new IrFpChart()
  ) {}
}

export interface IrFpCard {
  mainName: FpCardValueHeader;
  mainValue: string;
  mainColor?: string;
  secondName?: FpCardValueHeader;
  secondValue?: string;
}

export class IrFpChart {
  mainName: IrFpChartTitle;
  mainValue: string;
  first = new IrFpChartValue();
  second = new IrFpChartValue();
}

export class IrFpChartValue {
  name: string;
  value: string;
  pcnt: number;
  color: string;
  bgStyle: string;
}

export class IrFmvChartsSectionDTO {
  fmvSectionName: string;

  isFmvSectionSelected: boolean;
  isCompanyChartSelected: boolean;
  isIndustryChartSelected: boolean;
  isCountryChartSelected: boolean;
  isGroupChartSelected: boolean;
  isAssetsChartSelected: boolean;
}

export class IrValueCreationSectionDTO {
  valueCreationSectionName: string;

  isValueCreationSectionSelected: boolean;
  isPortfolioPerformanceChartSelected = true;
  isNetFundPerformanceChartSelected = true;

  chartPeriods: LocalDate[] = [];
}

export class InvestorPositionSectionDTO {
  isInvestorPositionSelected: boolean;
  investorsArray: IrInvestorPositionDataDTO[];
}

export class IrPieChartsSimpleData {
  commitmentAllocation: IrPieChartCard;
  opexDrillDown: IrPieChartCard;
}

export class IrPieChartCard {
  title: string;
  mainValue: string;
  finData: IrPieChartFinData[];
  pieStyle: PieStyle;
  aboveCommitment: IrPieChartFinData; // should have another html formating
  emptyState: PieChartEmptyState;
}

export class IrPieChartFinData {
  color: string;
  name: string;
  value: string;
  pcnt: string;
}

export interface PieStyle {
  'background-image': string;
}

export interface PieChartEmptyState {
  empty: boolean;
  title?: string;
  subtitle?: string;
}

export class IrValueCreationChartsPreData {
  constructor(
    public fundUUID: string,
    public reportedPeriod: LocalDate,
    public periods: LocalDate[],
    public password: ReportPasswordWrapper
  ) {}
}

export class ReportPasswordWrapper {
  pass: string;
}

export class IrInvestorPositionDataDTO {
  constructor(
    public investorId: string = '',
    public investorName: string = '',
    public investorCommitment: number = 0,
    public investorHolding: number = 0,
    public isInvestorSelected: boolean = false,
  ) {}
}

export class ShowInvestorPositionBulkUpload extends DefaultBulkUploadShow {
  parsedInvestorPositions: IrInvestorPositionDataDTO[];
  tableData: boolean; // if table data exist
  table: boolean; // show or hide table
}

export class IrAccountIdsDto {
  constructor(public accountIds: string[] = []) {}
}

export type IrFpChartTitle = 'Fair value' | 'NAV (Contribution + net gain)' | 'NAV (Contribution + net gain - carry)';
export type IrCompaniesStatus = 'Active' | 'Partially realized' | 'Realized';
